/**
 * Centralized routing security system
 * All route access checks must go through this system
 */

import { ROUTE_NAMES, ROUTE_PATHS } from '@/constants/routes'
import type { useAuthStore } from '@/stores/authStore'
import type { RouteLocationNormalized } from 'vue-router'

/**
 * Route security levels (mutually exclusive)
 */
export enum RouteSecurityLevel {
  /** Public routes - accessible to everyone */
  PUBLIC = 'public',
  /** Guest routes - only for unauthenticated users */
  GUEST_ONLY = 'guest_only',
  /** Protected routes - only for authenticated users */
  PROTECTED = 'protected',
  /** 2FA routes - special authentication state */
  TWO_FA_VERIFICATION = '2fa_verification'
}

/**
 * Security check result
 */
export interface SecurityCheckResult {
  allowed: boolean
  redirectTo?: { name: string; query?: Record<string, any> }
  reason?: string
}

/**
 * Public routes (no authentication required)
 */
const PUBLIC_ROUTES = new Set<string>([
  ROUTE_PATHS.HOME,
  ROUTE_PATHS.PUBLIC_POOL,
  ROUTE_PATHS.CONNECT_WALLET,
  ROUTE_PATHS.CONFIRM_WALLET,
  ROUTE_PATHS.DOCUMENTATION,
  ROUTE_PATHS.CONTACTS,
  ROUTE_PATHS.NOT_FOUND
])

/**
 * Public route patterns
 */
const PUBLIC_ROUTE_PATTERNS = [
  /^\/public-pool($|\?)/, // All public-pool nested routes
  /^\/user\/[^/]+($|\?)/, // User profile routes (but not /user/password)
  /^\/privacy($|\?)/, // Privacy policy
  /^\/terms($|\?)/ // Terms of service
]

/**
 * Guest routes (only for unauthenticated users)
 */
const GUEST_ONLY_ROUTES = new Set<string>([
  ROUTE_PATHS.LOGIN,
  ROUTE_PATHS.SIGNUP,
  ROUTE_PATHS.RESET_PASSWORD,
  ROUTE_PATHS.NEW_PASSWORD,
  ROUTE_PATHS.CONFIRM_EMAIL
])

/**
 * Protected routes (require authentication)
 */
const PROTECTED_ROUTES = new Set<string>([
  ROUTE_PATHS.STATISTICS,
  ROUTE_PATHS.PROFILE,
  ROUTE_PATHS.SETTINGS
])

/**
 * 2FA routes (special state)
 */
const TWO_FA_ROUTES = new Set<string>([ROUTE_PATHS.VERIFY_2FA])

/**
 * Determine route security level
 */
export function determineRouteSecurityLevel(route: RouteLocationNormalized): RouteSecurityLevel {
  const path = route.path

  // Check meta first (has priority)
  if (route.meta?.verify2fa === true) {
    return RouteSecurityLevel.TWO_FA_VERIFICATION
  }

  if (route.meta?.guest === true) {
    return RouteSecurityLevel.GUEST_ONLY
  }

  if (route.meta?.protected === true || route.meta?.requiresAuth === true) {
    return RouteSecurityLevel.PROTECTED
  }

  if (route.meta?.public === true) {
    return RouteSecurityLevel.PUBLIC
  }

  // Check by lists and patterns
  if (TWO_FA_ROUTES.has(path)) {
    return RouteSecurityLevel.TWO_FA_VERIFICATION
  }

  if (GUEST_ONLY_ROUTES.has(path)) {
    return RouteSecurityLevel.GUEST_ONLY
  }

  if (PROTECTED_ROUTES.has(path)) {
    return RouteSecurityLevel.PROTECTED
  }

  if (PUBLIC_ROUTES.has(path) || PUBLIC_ROUTE_PATTERNS.some((pattern) => pattern.test(path))) {
    return RouteSecurityLevel.PUBLIC
  }

  // CRITICAL: unrecognized routes are treated as protected by default
  console.warn(`Route ${path} has no explicit security level. Treating as PROTECTED.`)
  return RouteSecurityLevel.PROTECTED
}

/**
 * Check route access based on authentication state
 */
export function checkRouteAccess(
  route: RouteLocationNormalized,
  authStore: ReturnType<typeof useAuthStore>
): SecurityCheckResult {
  const securityLevel = determineRouteSecurityLevel(route)
  const { isAuthenticated, isLoggedIn, has2fa, needsToVerify2FA } = authStore

  switch (securityLevel) {
    case RouteSecurityLevel.PUBLIC:
      return { allowed: true }

    case RouteSecurityLevel.GUEST_ONLY:
      if (isAuthenticated) {
        return {
          allowed: false,
          redirectTo: { name: ROUTE_NAMES.STATISTICS },
          reason: 'Already authenticated - redirecting away from guest page'
        }
      }
      return { allowed: true }

    case RouteSecurityLevel.PROTECTED:
      if (!isAuthenticated) {
        return {
          allowed: false,
          redirectTo: { name: ROUTE_NAMES.LOGIN, query: { redirect: route.fullPath } },
          reason: 'Authentication required'
        }
      }

      if (needsToVerify2FA) {
        return {
          allowed: false,
          redirectTo: { name: ROUTE_NAMES.VERIFY_2FA },
          reason: '2FA verification required'
        }
      }

      return { allowed: true }

    case RouteSecurityLevel.TWO_FA_VERIFICATION:
      if (!isLoggedIn) {
        return {
          allowed: false,
          redirectTo: { name: ROUTE_NAMES.LOGIN },
          reason: 'Must be logged in for 2FA verification'
        }
      }

      if (isAuthenticated) {
        return {
          allowed: false,
          redirectTo: { name: ROUTE_NAMES.STATISTICS },
          reason: 'Already fully authenticated'
        }
      }

      if (!has2fa) {
        return {
          allowed: false,
          redirectTo: { name: ROUTE_NAMES.STATISTICS },
          reason: '2FA not enabled for user'
        }
      }

      return { allowed: true }

    default:
      console.error(`Unknown security level: ${securityLevel}`)
      return {
        allowed: false,
        redirectTo: { name: ROUTE_NAMES.LOGIN },
        reason: 'Unknown security level - denying access'
      }
  }
}

/**
 * Validate security configuration integrity
 */
export function validateSecurityConfiguration(): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  // Check intersections between sets
  const allRouteSets = [PUBLIC_ROUTES, GUEST_ONLY_ROUTES, PROTECTED_ROUTES, TWO_FA_ROUTES]
  const allRoutes = new Set<string>()

  for (const routeSet of allRouteSets) {
    for (const route of routeSet) {
      if (allRoutes.has(route)) {
        errors.push(`Duplicate route configuration: ${route}`)
      }
      allRoutes.add(route)
    }
  }

  // Check critical protected routes
  const criticalRoutes = [ROUTE_PATHS.STATISTICS, ROUTE_PATHS.PROFILE, ROUTE_PATHS.SETTINGS]
  for (const route of criticalRoutes) {
    if (!PROTECTED_ROUTES.has(route)) {
      errors.push(`Critical route ${route} is not properly protected`)
    }
  }

  // Check critical guest routes
  const criticalGuestRoutes = [ROUTE_PATHS.LOGIN, ROUTE_PATHS.SIGNUP]
  for (const route of criticalGuestRoutes) {
    if (!GUEST_ONLY_ROUTES.has(route)) {
      errors.push(`Auth route ${route} should be guest-only`)
    }
  }

  // Check that all defined routes are accounted for
  const allDefinedPaths = Object.values(ROUTE_PATHS) as string[]
  const allConfiguredPaths = new Set([...allRoutes])

  for (const path of allDefinedPaths) {
    if (
      !allConfiguredPaths.has(path) &&
      !PUBLIC_ROUTE_PATTERNS.some((pattern) => pattern.test(path))
    ) {
      console.warn(`Route ${path} is defined but not configured in security sets`)
    }
  }

  return {
    valid: errors.length === 0,
    errors
  }
}
